import axios from "axios";

export const baseURL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";

const api = axios.create({
  baseURL,
});

const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('bearer');

  window.dispatchEvent(new Event("authLogout")); 

  window.location.href = '/login'; 
};


// Login function
export const login = async (email, password) => {
  const loginPayload = {
    email: email?.trim() || '',
    password: password || '',
  };

  try {
    const response = await api.post('accounts/login/organizational/', loginPayload);
    const data = response.data;

    if (data.is_deactivated) {
      return {
        success: false,
        is_deactivated: true,
        deactivation_date: data.deactivation_date,
        email: data.email,
        message: data.message || "Account is deactivated"
      };
    }

    if (data.access && data.refresh) {
      // Store tokens in localStorage
      try {
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        localStorage.setItem('bearer', `Bearer ${data.access}`);
        
        const storedAccess = localStorage.getItem('accessToken');
        const storedRefresh = localStorage.getItem('refreshToken');
        
        if (!storedAccess || !storedRefresh) {
          throw new Error('Failed to store tokens');
        }
        
        return { 
          success: true, 
          message: "Login successful",
          tokens: { access: data.access, refresh: data.refresh }
        };
      } catch (storageError) {
        console.error('Storage error:', storageError);
        return { success: false, message: "Failed to store authentication tokens" };
      }
    }
    return { success: false, message: "Authentication failed: Missing tokens" };
  } catch (error) {
    console.error('API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error occurred' 
    };
  }
};

// services/api.js
export const otpLogin = async (email, otp) => {
  try {
    const response = await api.post('accounts/auth/otp-login/', { email, otp });
    const data = response.data;

    if (data.is_deactivated) {
      return {
        success: false,
        is_deactivated: true,
        deactivation_date: data.deactivation_date,
        email: data.email,
        message: data.message || "Account is deactivated"
      };
    }

    if (data.access && data.refresh) {
      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);
      localStorage.setItem('bearer', `Bearer ${data.access}`);
      return {
        success: true,
        tokens: { access: data.access, refresh: data.refresh }
      };
    } else {
      return { success: false, message: "Missing tokens" };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'OTP login failed'
    };
  }
};



// Send OTP
export const sendOtp = async (email, action = 'registration') => {
  try {
    const response = await api.post('accounts/send-otp/', { email, action });
    return { success: true, message: response.data.message };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send OTP"
    };
  }
};


// Verify OTP
export const verifyOtp = async (email, otp) => {
  try {
    const response = await api.post('accounts/verify-otp/', { email, otp });
    return {
      success: true,
      message: "OTP verified successfully",
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "OTP verification failed"
    };
  }
};



// Add a request interceptor to include the access token
// api.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem('accessToken');
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = localStorage.getItem('i18nextLng') || 'en'; // ← add this
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    const detailMsg = typeof error.response?.data?.detail === 'string' ? error.response.data.detail.toLowerCase() : '';
    const isDeletedOrInactive =
      error.response?.data?.code === 'user_deleted' ||
      error.response?.data?.is_deleted === true ||
      detailMsg.includes('deleted') ||
      detailMsg.includes('deactivated');

    if (isDeletedOrInactive) {
      logout();
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      if (originalRequest._retry) {
        logout();
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${baseURL}accounts/token/refresh/`, { refresh: refreshToken });
          if (response.status === 200 && response.data.access) {
            const { access } = response.data;
            localStorage.setItem('accessToken', access);
            localStorage.setItem('bearer', `Bearer ${access}`);
            originalRequest.headers.Authorization = `Bearer ${access}`;
            return api(originalRequest);
          }
        }
        logout();
      } catch (err) {
        console.error("Error refreshing token:", err);
        logout();
      }
    }

    // ── 403: subscription expired (set by SubscriptionGuardMiddleware) ────
    if (error.response?.status === 403 && error.response?.data?.subscription_expired === true) {
      const data = error.response.data;

      // Import Zustand store outside React — works because Zustand stores
      // are module singletons, not React context
      const { setPlanExpiredPopup } = (await import('../store/Useplanexpiredpopupstore')).default.getState();

      setPlanExpiredPopup({
        showPopup: true,
        isFreePlan: data.plan_type === 'free',
        extensionRequested: data.extension_requested ?? false,
      });

      // Resolve silently — the popup handles the UX, no need to throw
      return Promise.resolve({ data: { success: false, subscription_expired: true } });
    }

    // ── 403: deactivated account ────
    if (error.response?.status === 403 && error.response?.data?.is_deactivated === true) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('bearer');
      window.dispatchEvent(new Event("authLogout")); 
      window.location.href = '/deactivated-account';
      return Promise.resolve({ data: { success: false, is_deactivated: true } });
    }

    return Promise.reject(error);
  }
);

export default api;
